import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { StarIcon, CheckCircleIcon } from './Icons';

const PointsDisplay = ({ onCheckIn, showCheckin = true, compact = false }) => {
  const { user } = useAuth();
  const [pointsData, setPointsData] = useState({
    total_xp: 0,
    current_streak: 0,
    badges: {},
    loading: true,
    canCheckIn: false
  });
  const [isCheckingIn, setIsCheckingIn] = useState(false);

  // Fetch user points and achievements
  useEffect(() => {
    const fetchPointsData = async () => {
      if (!user?.id) return;

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/points/history/${user.id}?limit=1`);

        if (response.ok) {
          const data = await response.json();
          setPointsData(prev => ({
            ...prev,
            total_xp: data.total_xp || 0,
            current_streak: data.current_streak || 0,
            loading: false
          }));
        }
      } catch (error) {
        console.error('Error fetching points data:', error);
        setPointsData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchPointsData();
  }, [user?.id]);

  // Handle daily check-in
  const handleCheckIn = async () => {
    if (!user?.id || isCheckingIn) return;

    setIsCheckingIn(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id
        })
      });

      const data = await response.json();

      if (data.success && !data.already_checked_in) {
        // Update local state with new points and streak
        setPointsData(prev => ({
          ...prev,
          total_xp: data.total_xp,
          current_streak: data.streak
        }));

        // Show success message
        if (onCheckIn) {
          onCheckIn({
            success: true,
            points: data.points_awarded,
            streak: data.streak,
            message: data.message
          });
        }
      } else if (data.already_checked_in) {
        if (onCheckIn) {
          onCheckIn({
            success: false,
            message: data.message
          });
        }
      }
    } catch (error) {
      console.error('Error during check-in:', error);
      if (onCheckIn) {
        onCheckIn({
          success: false,
          message: 'Check-in failed. Please try again.'
        });
      }
    } finally {
      setIsCheckingIn(false);
    }
  };

  if (pointsData.loading) {
    return null;
  }

  // Compact version for hero section
  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: '1rem 1.5rem', marginTop: '1rem' }}>
        {/* XP Points */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <StarIcon className="w-5 h-5" color="white" />
          </div>
          <div>
            <p className="mc-caption" style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '0.125rem' }}>
              XP POINTS
            </p>
            <p className="mc-heading-3" style={{ color: 'white', marginBottom: 0 }}>
              {pointsData.total_xp.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Streak */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '1.25rem' }}>🔥</span>
          </div>
          <div>
            <p className="mc-caption" style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '0.125rem' }}>
              STREAK
            </p>
            <p className="mc-heading-3" style={{ color: 'white', marginBottom: 0 }}>
              {pointsData.current_streak} {pointsData.current_streak === 1 ? 'day' : 'days'}
            </p>
          </div>
        </div>

        {/* Check-in Button */}
        {showCheckin && (
          <button
            onClick={handleCheckIn}
            disabled={isCheckingIn}
            className="mc-button mc-button-outline"
            style={{
              borderColor: 'white',
              color: 'white',
              background: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = 'var(--mc-primary)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = 'white';
            }}
          >
            {isCheckingIn ? (
              'Checking in...'
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4" color="currentColor" />
                Daily Check-in
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  // Original card version for stats grid
  return (
    <div className="mc-stat-card">
      <div className="mc-stat-icon">
        <StarIcon className="w-6 h-6" color="var(--mc-primary)" />
      </div>
      <div>
        <div className="mc-stat-value" style={{ color: 'var(--mc-primary)' }}>
          {pointsData.total_xp.toLocaleString()}
        </div>
        <div className="mc-stat-label">XP Points</div>
        {pointsData.current_streak > 0 && (
          <div className="mc-caption" style={{ color: 'var(--mc-primary)', marginTop: '0.25rem' }}>
            🔥 {pointsData.current_streak} day streak
          </div>
        )}
      </div>
    </div>
  );
};

export default PointsDisplay;
